import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Briefcase,
  Palette,
  HardDrive,
  DollarSign,
  FileText,
  Sparkles,
  Gauge,
  Settings
} from 'lucide-react';

const settingsSections = [
  {
    title: 'App Profiles',
    description: 'App identity, logo, Brand Kits, Connected Accounts, App Dropbox, Connected Platforms',
    icon: Briefcase,
    color: 'cyan',
    path: '/app/admin/app-profile'
  },
  {
    title: 'Platform Identity',
    description: 'Platform UI branding — logo, colors, footer, system emails',
    icon: Palette,
    color: 'purple',
    path: '/app/admin/product-skin',
    badge: 'Singleton'
  },
  {
    title: 'Storage & Backups',
    description: 'Cloud storage connections, backup settings, and file management',
    icon: HardDrive,
    color: 'blue',
    path: '/settings/storage'
  },
  {
    title: 'Pricing & Access Control',
    description: 'Configure subscription tiers, features, and access rules',
    icon: DollarSign,
    color: 'green',
    path: '/admin/pricing-settings'
  },
  {
    title: 'Landing Pages',
    description: 'Edit public-facing pages and funnels (syncs with public landing)',
    icon: FileText,
    color: 'orange',
    path: '/admin/landing-page'
  },
  {
    title: 'Self-Marketing Engine',
    description: 'Draft-only admin engine (per App Profile)',
    icon: Sparkles,
    color: 'yellow',
    path: '/app/admin/self-marketing',
    badge: 'Admin Only'
  },
  {
    title: 'System Control Center',
    description: 'Global configuration, routing, and health checks',
    icon: Gauge,
    color: 'gray',
    path: '/app/admin/system-settings'
  }
];

export default function AppSettings() {
  const navigate = useNavigate();

  const getColorClasses = (color) => {
    const colors = {
      cyan: 'text-cyan-500',
      pink: 'text-pink-500',
      green: 'text-green-500',
      blue: 'text-blue-500',
      purple: 'text-purple-500',
      orange: 'text-orange-500',
      indigo: 'text-indigo-500',
      yellow: 'text-yellow-500',
      red: 'text-red-500',
      gray: 'text-gray-500'
    };
    return colors[color] || 'text-white';
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">App Settings</h1>
        <p className="text-[#8A93A6]">Global configuration - affects all users</p>
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm text-blue-400">
            <strong>Rule:</strong> If it affects more than one user → it lives here
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsSections.map((section) => {
          const Icon = section.icon;
          return (
            <div
              key={section.path}
              onClick={() => navigate(section.path)}
              className="cursor-pointer"
            >
              <Card className="glass hover:bg-white/5 transition-all h-full">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <Icon className={`h-6 w-6 ${getColorClasses(section.color)}`} />
                    {section.badge && (
                      <span className="text-xs px-2 py-1 bg-pink-500/10 text-pink-500 rounded">
                        {section.badge}
                      </span>
                    )}
                  </div>
                  <CardTitle className="text-base">{section.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{section.description}</CardDescription>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      <Card className="glass mt-6">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Settings className="h-5 w-5 text-[#8A93A6] mt-0.5" />
            <div>
              <p className="text-sm font-medium text-white mb-1">About App Settings</p>
              <p className="text-sm text-[#8A93A6]">
                This is the centralized hub for all global configuration. Any setting that impacts 
                multiple users, system behavior, or app-wide features should be managed here rather 
                than in individual user settings.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
